import React, { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAssetTilesUrl } from "../../api/assets";
import { useUrbanAnimation } from "../../contexts/UrbanAnimationContext";
import { useMap } from "../../contexts/MapContext";

const SOURCE_ID = "urban-building-state";
const OUTLINE_ID = "urban-building-state-outline";
const ASSET_SOURCE_LAYER = "assets";

const FILL_PAINT = {
  "fill-color": [
    "case",
    ["==", ["feature-state", "urbanState"], "burning"], "#ff6b00",
    ["==", ["feature-state", "urbanState"], "burnt"], "#1a1a1a",
    "rgba(0,0,0,0)",
  ],
  "fill-opacity": [
    "case",
    ["==", ["feature-state", "urbanState"], "burning"], 0.9,
    ["==", ["feature-state", "urbanState"], "burnt"], 0.75,
    0,
  ],
};

const OUTLINE_PAINT = {
  "line-color": [
    "case",
    ["==", ["feature-state", "urbanState"], "burning"], "#ff4500",
    ["==", ["feature-state", "urbanState"], "burnt"], "#555555",
    "rgba(0,0,0,0)",
  ],
  "line-width": [
    "case",
    ["==", ["feature-state", "urbanState"], "burning"], 2.5,
    ["==", ["feature-state", "urbanState"], "burnt"], 1,
    0,
  ],
};

const UrbanBuildingStateLayer: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentFrame, timestamps, selectedIgnitionInfo } = useUrbanAnimation();
  const { data: tilesData } = useAssetTilesUrl(projectId);
  const { map } = useMap();

  const tilesUrl: string | null = (tilesData as any)?.data?.tiles_url ?? null;
  const assetIgnitionS: Record<string, number> = selectedIgnitionInfo?.urban_asset_ignition_s ?? {};
  const assetBurnoutS: Record<string, number> = selectedIgnitionInfo?.urban_asset_burnout_s ?? {};
  const hasUrbanData = !!tilesUrl && Object.keys(assetIgnitionS).length > 0;

  const frameElapsedS = useMemo(() => {
    if (!timestamps.length || currentFrame === null || currentFrame >= timestamps.length) return null;
    return timestamps[currentFrame].diff(timestamps[0], 'second');
  }, [timestamps, currentFrame]);

  // Track which asset IDs currently carry an `urbanState` feature-state so we
  // can clear stale ones when the user switches simulations (the new sim's
  // ignition map is a different subset of buildings) or scrubs backwards.
  const paintedAssetIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!map || !tilesUrl) return;

    const setupSourceAndLayers = () => {
      if (!map.isStyleLoaded()) return;

      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: "vector",
          tiles: [tilesUrl],
          minzoom: 0,
          // Must match the backend asset-tile max zoom (PMTILES_MAX_ZOOM = 12).
          // A higher value makes mapbox request missing z>12 tiles instead of
          // overzooming z12, so buildings disappear when zoomed in past 12.
          maxzoom: 12,
          promoteId: "__id",
        } as any);
        map.addLayer({ id: SOURCE_ID, type: "fill", source: SOURCE_ID, "source-layer": ASSET_SOURCE_LAYER, paint: FILL_PAINT } as any);
        map.addLayer({ id: OUTLINE_ID, type: "line", source: SOURCE_ID, "source-layer": ASSET_SOURCE_LAYER, paint: OUTLINE_PAINT } as any);
      }
    };

    if (map.isStyleLoaded()) {
      setupSourceAndLayers();
    } else {
      map.once("style.load", setupSourceAndLayers);
    }

    if (!map.getSource(SOURCE_ID)) return;

    const nextPainted = new Set<number>();
    if (hasUrbanData && frameElapsedS !== null) {
      for (const [assetId, ignS] of Object.entries(assetIgnitionS)) {
        if (frameElapsedS >= ignS) {
          const idNum = Number(assetId);
          nextPainted.add(idNum);
          const boS = assetBurnoutS[assetId];
          map.setFeatureState(
            { source: SOURCE_ID, sourceLayer: ASSET_SOURCE_LAYER, id: idNum },
            { urbanState: boS !== undefined && frameElapsedS >= boS ? "burnt" : "burning" },
          );
        }
      }
    }

    for (const prevId of paintedAssetIdsRef.current) {
      if (!nextPainted.has(prevId)) {
        map.removeFeatureState(
          { source: SOURCE_ID, sourceLayer: ASSET_SOURCE_LAYER, id: prevId },
          "urbanState",
        );
      }
    }
    paintedAssetIdsRef.current = nextPainted;
  }, [map, tilesUrl, frameElapsedS, assetIgnitionS, assetBurnoutS, hasUrbanData]);

  useEffect(() => {
    return () => {
      if (!map) return;
      try {
        if (map.getLayer(OUTLINE_ID)) map.removeLayer(OUTLINE_ID);
        if (map.getLayer(SOURCE_ID)) map.removeLayer(SOURCE_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch {
        /* map may be tearing down */
      }
      paintedAssetIdsRef.current = new Set();
    };
  }, [map]);

  return null;
};

export default UrbanBuildingStateLayer;
